import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, RotateCw, CheckCircle2, AlertTriangle, Loader2, Upload, Scissors } from "lucide-react";

// Maps an asset's cdn/hls status into a self-heal progress stage.
function stageOf(a) {
  if (!a) return { key: "pending", label: "…", tone: "zinc", spin: true };
  if (a.cdn_status === "failed") return { key: "failed", label: "Failed", tone: "red", spin: false };
  if (a.cdn_status === "queued" || a.cdn_status === "publishing")
    return { key: "queued", label: "Queued", tone: "amber", spin: true };
  const hls = a.hls_status;
  if (a.cdn_status === "ready" && (hls === "ready"))
    return { key: "ready", label: "Ready", tone: "emerald", spin: false };
  if (a.cdn_status === "ready")
    return { key: "regenerating", label: "Regenerating HLS", tone: "cyan", spin: true };
  return { key: "queued", label: "Queued", tone: "amber", spin: true };
}

const TONE = {
  zinc: "bg-zinc-800 text-zinc-400 border-zinc-700",
  amber: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  cyan: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/40",
  red: "bg-red-500/10 text-red-400 border-red-500/30",
};

/**
 * Self-contained Validate-R2 + self-heal tool. Renders its own buttons,
 * demoted list, and a LIVE per-asset progress readout after re-queue. No
 * outer panel chrome — callers wrap it (Monitoring panel / Assets section).
 */
export default function R2IntegrityTool() {
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState(null);
  const [requeuing, setRequeuing] = useState(false);
  const [requeued, setRequeued] = useState([]); // [{id,name}]
  const [progress, setProgress] = useState({}); // id -> asset status doc
  const pollRef = useRef(null);
  const [autoDemoted, setAutoDemoted] = useState(null); // [{id,name,missing_r2,has_source,status,cdn_error}]
  const [loadingDemoted, setLoadingDemoted] = useState(false);
  const [sweeps, setSweeps] = useState([]);
  const [sceneBackfill, setSceneBackfill] = useState({ running: false, processed: 0, total: 0, updated: 0 });
  const scenePollRef = useRef(null);

  const pollBackfill = async () => {
    try {
      const r = await api.post("/assets/scene-backfill-status");
      setSceneBackfill(r.data);
      if (r.data.running && !scenePollRef.current) {
        scenePollRef.current = setInterval(pollBackfill, 2500);
      }
      if (!r.data.running && scenePollRef.current) {
        clearInterval(scenePollRef.current); scenePollRef.current = null;
        if (r.data.total > 0) toast.success(`Scene backfill done · ${r.data.updated}/${r.data.total} assets got cuts`);
        loadDemoted();
      }
    } catch { /* ignore */ }
  };

  const startSceneBackfill = async () => {
    try {
      await api.post("/assets/detect-scenes-all?threshold=0.4");
      toast.success("Scene detection started for the whole library");
      setSceneBackfill((s) => ({ ...s, running: true }));
      if (scenePollRef.current) clearInterval(scenePollRef.current);
      scenePollRef.current = setInterval(pollBackfill, 2500);
    } catch (e) {
      toast.error(e?.response?.status === 409 ? "A scene backfill is already running" : "Could not start scene backfill");
    }
  };

  const loadSweeps = async () => {
    try {
      const r = await api.get("/r2/sweep-summary", { params: { limit: 12 } });
      setSweeps(r.data.runs || []);
    } catch { /* non-blocking */ }
  };
  const [rowBusy, setRowBusy] = useState({}); // id -> bool
  const [uploadPct, setUploadPct] = useState({}); // id -> 0..100 | "processing"

  const reuploadOne = async (assetId, file) => {
    if (!file) return;
    const ctype = file.type || "video/mp4";
    setUploadPct((p) => ({ ...p, [assetId]: 0 }));
    try {
      const init = await api.post("/assets/upload/init", {
        filename: file.name, content_type: ctype, total_size: file.size, attach_asset_id: assetId,
      });
      const uploadId = init.data.upload_id;
      const chunkSize = init.data.chunk_size || 8 * 1024 * 1024;
      const totalChunks = Math.ceil(file.size / chunkSize);
      let index = init.data.next_index || 0;
      let sent = index * chunkSize;
      while (index < totalChunks) {
        const start = index * chunkSize;
        const blob = file.slice(start, Math.min(file.size, start + chunkSize));
        const r = await api.put(`/assets/upload/${uploadId}/chunk?index=${index}`, blob, {
          headers: { "Content-Type": "application/octet-stream" }, timeout: 120000,
        });
        sent = r.data.received_bytes; index = r.data.next_index;
        setUploadPct((p) => ({ ...p, [assetId]: Math.round((sent / file.size) * 100) }));
      }
      setUploadPct((p) => ({ ...p, [assetId]: "processing" }));
      await api.post(`/assets/upload/${uploadId}/complete`, {}, { timeout: 600000 });
      toast.success(`Re-uploaded source for "${file.name}" — regenerating in the background`);
      setUploadPct((p) => { const n = { ...p }; delete n[assetId]; return n; });
      loadDemoted();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-upload failed");
      setUploadPct((p) => { const n = { ...p }; delete n[assetId]; return n; });
    }
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const loadDemoted = async () => {
    setLoadingDemoted(true);
    try {
      const r = await api.post("/assets/demoted");
      setAutoDemoted(r.data.demoted || []);
    } catch { /* non-blocking */ }
    finally { setLoadingDemoted(false); }
  };

  useEffect(() => { loadDemoted(); loadSweeps(); pollBackfill(); }, []);
  useEffect(() => () => scenePollRef.current && clearInterval(scenePollRef.current), []);

  const requeueOne = async (id, name) => {
    setRowBusy((b) => ({ ...b, [id]: true }));
    try {
      const r = await api.post("/assets/requeue-r2", { asset_ids: [id] });
      if (r.data.requeued_count > 0) {
        toast.success(`Re-transcoding "${name || id}" — regenerating in the background`);
        setRequeued((prev) => prev.some((x) => x.id === id) ? prev : [...prev, { id, name }]);
        const rids = [...requeued.map((x) => x.id), id];
        await pollProgress(rids);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => pollProgress(rids), 3000);
      } else {
        toast.info(`"${name || id}" has no surviving source — a manual re-upload is needed`);
      }
    } catch (e) {
      toast.error(e?.response?.status === 503 ? "R2 not configured" : "Re-transcode failed");
    } finally {
      setRowBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const runValidate = async () => {
    setValidating(true);
    setRequeued([]);
    setProgress({});
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    try {
      const r = await api.post("/assets/validate-r2");
      setResult(r.data);
      if (r.data.demoted_count === 0) toast.success(`All ${r.data.checked} R2 asset(s) healthy`);
      else toast.warning(`${r.data.demoted_count} broken R2 asset(s) demoted to failed`);
    } catch {
      toast.error("R2 validation failed");
    } finally {
      setValidating(false);
      loadDemoted();
      loadSweeps();
    }
  };

  const pollProgress = async (ids) => {
    try {
      const r = await api.post("/assets/status", { asset_ids: ids });
      const map = {};
      (r.data.assets || []).forEach((a) => { map[a.id] = a; });
      setProgress(map);
      const allDone = ids.every((id) => {
        const st = stageOf(map[id]).key;
        return st === "ready" || st === "failed";
      });
      if (allDone && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    } catch { /* transient */ }
  };

  const runRequeue = async () => {
    if (!result) return;
    const ids = (result.demoted || []).filter((d) => d.has_source).map((d) => d.id);
    if (!ids.length) return;
    setRequeuing(true);
    try {
      const r = await api.post("/assets/requeue-r2", { asset_ids: ids });
      const list = r.data.requeued || [];
      setRequeued(list);
      toast.success(`Re-queued ${r.data.requeued_count} asset(s) — regenerating in the background`);
      if (r.data.needs_reupload_count > 0)
        toast.info(`${r.data.needs_reupload_count} asset(s) need a manual re-upload (no source)`);
      const rids = list.map((x) => x.id);
      if (rids.length) {
        await pollProgress(rids);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => pollProgress(rids), 3000);
      }
    } catch (e) {
      toast.error(e?.response?.status === 503 ? "R2 not configured" : "Re-queue failed");
    } finally {
      setRequeuing(false);
    }
  };

  const requeueable = result ? (result.demoted || []).filter((d) => d.has_source).length : 0;

  const runRequeueAllDemoted = async () => {
    const ids = (autoDemoted || []).filter((d) => d.has_source).map((d) => d.id);
    if (!ids.length) return;
    setRequeuing(true);
    try {
      const r = await api.post("/assets/requeue-r2", { asset_ids: ids });
      const list = r.data.requeued || [];
      setRequeued((prev) => {
        const seen = new Set(prev.map((x) => x.id));
        return [...prev, ...list.filter((x) => !seen.has(x.id))];
      });
      toast.success(`Re-transcoding ${r.data.requeued_count} demoted asset(s) — regenerating in the background`);
      if (r.data.needs_reupload_count > 0)
        toast.info(`${r.data.needs_reupload_count} asset(s) need a manual re-upload (no source)`);
      const rids = list.map((x) => x.id);
      if (rids.length) {
        await pollProgress(rids);
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(() => pollProgress(rids), 3000);
      }
    } catch (e) {
      toast.error(e?.response?.status === 503 ? "R2 not configured" : "Re-transcode failed");
    } finally {
      setRequeuing(false);
    }
  };

  const [retranscoding, setRetranscoding] = useState(false);
  const runRetranscodeAll = async () => {
    if (!window.confirm("Re-transcode ALL assets to the new constant-frame-rate / fixed-GOP encoding?\n\nThis regenerates every asset's HLS in the background (runs one at a time). Playback stays on the old version until each finishes.")) return;
    setRetranscoding(true);
    try {
      const r = await api.post("/assets/retranscode-all");
      toast.success(`Queued ${r.data.queued} asset(s) for CFR re-transcode — running in the background`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-transcode failed to start");
    } finally {
      setRetranscoding(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="r2-integrity-tool">
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={runValidate}
          disabled={validating}
          data-testid="validate-r2-btn"
          className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm border border-emerald-500/60 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-40 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${validating ? "animate-spin" : ""}`} />
          {validating ? "Validating…" : "Validate R2"}
        </button>
        <button
          onClick={runRetranscodeAll}
          disabled={retranscoding}
          data-testid="retranscode-all-btn"
          className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm border border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10 disabled:opacity-40 flex items-center gap-2"
          title="Regenerate every asset's HLS with the new constant-frame-rate / fixed-GOP encoding"
        >
          <RotateCw className={`w-4 h-4 ${retranscoding ? "animate-spin" : ""}`} />
          {retranscoding ? "Queuing…" : "Re-transcode all (CFR)"}
        </button>
        <button
          onClick={startSceneBackfill}
          disabled={sceneBackfill.running}
          data-testid="scene-backfill-btn"
          className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm border border-fuchsia-500/60 text-fuchsia-300 hover:bg-fuchsia-500/10 disabled:opacity-40 flex items-center gap-2"
          title="Detect scene cuts for every asset that has none yet, in the background"
        >
          <Scissors className={`w-4 h-4 ${sceneBackfill.running ? "animate-pulse" : ""}`} />
          {sceneBackfill.running
            ? `Detecting scenes… ${sceneBackfill.processed}/${sceneBackfill.total}`
            : "Detect scenes (all)"}
        </button>
        {result && requeueable > 0 && (
          <button
            onClick={runRequeue}
            disabled={requeuing}
            data-testid="requeue-r2-btn"
            className="font-mono text-[11px] uppercase tracking-widest px-3 py-2 rounded-sm border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 disabled:opacity-40 flex items-center gap-2"
          >
            <RotateCw className={`w-4 h-4 ${requeuing ? "animate-spin" : ""}`} />
            {requeuing ? "Re-queuing…" : `Re-queue ${requeueable} with source`}
          </button>
        )}
      </div>

      {!result && (!autoDemoted || autoDemoted.length === 0) && (
        <p className="text-xs text-zinc-500 italic" data-testid="r2-integrity-hint">
          HEAD-checks every <span className="text-zinc-400">ready</span> asset's R2 URL. Any returning 4xx are
          demoted to <span className="text-red-400">failed</span> so they stop airing; those whose source
          survives can be re-queued to regenerate. Assets the live pusher finds missing on R2 appear below automatically.
        </p>
      )}

      {!result && autoDemoted && autoDemoted.length > 0 && (
        <div className="space-y-2" data-testid="r2-auto-demoted">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[11px] uppercase tracking-widest text-red-400">
              {autoDemoted.length} demoted asset{autoDemoted.length === 1 ? "" : "s"} · needs recovery
            </span>
            <div className="flex items-center gap-2">
              {autoDemoted.some((d) => d.has_source) && (
                <button
                  onClick={runRequeueAllDemoted}
                  disabled={requeuing}
                  data-testid="retranscode-all-demoted-btn"
                  className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 disabled:opacity-40 flex items-center gap-1"
                  title="Re-upload the source and regenerate HLS for every demoted asset that still has a source"
                >
                  <RotateCw className={`w-3 h-3 ${requeuing ? "animate-spin" : ""}`} />
                  {requeuing ? "Re-transcoding…" : `Re-transcode all ${autoDemoted.filter((d) => d.has_source).length} with source`}
                </button>
              )}
              <button
                onClick={loadDemoted}
                disabled={loadingDemoted}
                data-testid="refresh-demoted-btn"
                className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-zinc-700 text-zinc-400 hover:bg-zinc-800 disabled:opacity-40 flex items-center gap-1"
              >
                <RefreshCw className={`w-3 h-3 ${loadingDemoted ? "animate-spin" : ""}`} /> Refresh
              </button>
            </div>
          </div>
          <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-900 max-h-72 overflow-auto" data-testid="r2-auto-demoted-list">
            {autoDemoted.map((d) => {
              const live = progress[d.id];
              const stage = requeued.some((x) => x.id === d.id) ? stageOf(live) : null;
              return (
                <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`r2-auto-demoted-row-${d.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                    <span className="truncate text-zinc-200">{d.name || d.id}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 font-mono text-[10px]">
                    {stage ? (
                      <span className={`px-1.5 py-0.5 rounded-sm border flex items-center gap-1 ${TONE[stage.tone]}`} data-testid={`r2-auto-progress-${d.id}`}>
                        {stage.spin ? <Loader2 className="w-3 h-3 animate-spin" /> : stage.key === "ready" ? <CheckCircle2 className="w-3 h-3" /> : null}
                        {stage.label}
                      </span>
                    ) : (
                      <>
                        {d.missing_r2 ? (
                          <span className="px-1.5 py-0.5 rounded-sm bg-red-500/10 text-red-400 border border-red-500/30" data-testid={`r2-missing-badge-${d.id}`} title={d.cdn_error || "R2 object missing"}>
                            missing on R2{d.has_source ? "" : " — re-upload"}
                          </span>
                        ) : (
                          <span className="px-1.5 py-0.5 rounded-sm bg-red-500/10 text-red-400 border border-red-500/30">{d.status ? `HTTP ${d.status}` : "failed"}</span>
                        )}
                        {d.has_source ? (
                          <button
                            onClick={() => requeueOne(d.id, d.name)}
                            disabled={!!rowBusy[d.id]}
                            data-testid={`r2-retranscode-btn-${d.id}`}
                            className="px-2 py-0.5 rounded-sm border border-amber-500/60 text-amber-400 hover:bg-amber-500/10 disabled:opacity-40 flex items-center gap-1"
                            title="Re-upload the source to R2 and regenerate the HLS ladder"
                          >
                            <RotateCw className={`w-3 h-3 ${rowBusy[d.id] ? "animate-spin" : ""}`} /> Re-transcode
                          </button>
                        ) : (
                          uploadPct[d.id] !== undefined ? (
                            <span className="px-1.5 py-0.5 rounded-sm bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 flex items-center gap-1" data-testid={`r2-reupload-progress-${d.id}`}>
                              <Loader2 className="w-3 h-3 animate-spin" />
                              {uploadPct[d.id] === "processing" ? "Processing…" : `Uploading ${uploadPct[d.id]}%`}
                            </span>
                          ) : (
                            <label className="px-2 py-0.5 rounded-sm border border-cyan-500/60 text-cyan-300 hover:bg-cyan-500/10 cursor-pointer flex items-center gap-1" data-testid={`r2-reupload-btn-${d.id}`} title="Upload a fresh source file to restore this asset">
                              <Upload className="w-3 h-3" /> Re-upload
                              <input
                                type="file"
                                accept="video/*"
                                className="hidden"
                                data-testid={`r2-reupload-input-${d.id}`}
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; reuploadOne(d.id, f); }}
                              />
                            </label>
                          )
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {result && (
        <div className="space-y-3" data-testid="r2-validate-result">
          <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px]">
            <span className="text-zinc-500">checked <span className="text-zinc-200">{result.checked}</span>/{result.total_ready_with_r2}</span>
            <span className="text-zinc-500">demoted <span className={result.demoted_count ? "text-red-400" : "text-emerald-400"}>{result.demoted_count}</span></span>
            <span className="text-zinc-500">re-queueable <span className="text-amber-400">{result.requeueable_count}</span></span>
          </div>

          {result.demoted_count === 0 ? (
            <div className="flex items-center gap-2 text-xs text-emerald-400" data-testid="r2-all-healthy">
              <CheckCircle2 className="w-4 h-4 shrink-0" /> Every R2 asset resolved — nothing demoted.
            </div>
          ) : (
            <div className="border border-zinc-800 rounded-sm divide-y divide-zinc-900 max-h-64 overflow-auto" data-testid="r2-demoted-list">
              {result.demoted.map((d) => {
                const live = progress[d.id];
                const stage = requeued.some((x) => x.id === d.id) ? stageOf(live) : null;
                return (
                  <div key={d.id} className="flex items-center justify-between gap-2 px-3 py-2 text-xs" data-testid={`r2-demoted-row-${d.id}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
                      <span className="truncate text-zinc-200">{d.name || d.id}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 font-mono text-[10px]">
                      {stage ? (
                        <span
                          className={`px-1.5 py-0.5 rounded-sm border flex items-center gap-1 ${TONE[stage.tone]}`}
                          data-testid={`r2-progress-${d.id}`}
                        >
                          {stage.spin ? <Loader2 className="w-3 h-3 animate-spin" /> : stage.key === "ready" ? <CheckCircle2 className="w-3 h-3" /> : null}
                          {stage.label}
                        </span>
                      ) : (
                        <>
                          <span className="px-1.5 py-0.5 rounded-sm bg-red-500/10 text-red-400 border border-red-500/30">HTTP {d.status}</span>
                          {d.has_source ? (
                            <span className="px-1.5 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 border border-amber-500/30">has source</span>
                          ) : (
                            <span className="px-1.5 py-0.5 rounded-sm bg-zinc-800 text-zinc-400 border border-zinc-700">re-upload</span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {requeued.length > 0 && (
            <div className="font-mono text-[10px] text-zinc-500" data-testid="r2-requeue-note">
              Regenerating {requeued.length} asset(s) — R2 original then adaptive HLS. Status updates live above.
            </div>
          )}
        </div>
      )}
      {sweeps.length > 0 && (
        <div className="pt-3 border-t border-zinc-800/60 space-y-1.5" data-testid="r2-sweep-history">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Integrity history · checked / demoted per run</div>
          {(() => {
            const series = [...sweeps].reverse(); // chronological (oldest -> newest)
            const max = Math.max(1, ...series.map((s) => s.demoted_count || 0));
            const W = 5, GAP = 2, H = 26;
            return (
              <svg
                width={series.length * (W + GAP)}
                height={H}
                className="block"
                data-testid="r2-sweep-sparkline"
                role="img"
                aria-label="Demoted assets per integrity run"
              >
                {series.map((s, i) => {
                  const d = s.demoted_count || 0;
                  const h = d > 0 ? Math.max(3, Math.round((d / max) * (H - 2))) : 1;
                  return (
                    <rect
                      key={i}
                      x={i * (W + GAP)}
                      y={H - h}
                      width={W}
                      height={h}
                      rx="1"
                      className={d > 0 ? "fill-red-400/80" : "fill-emerald-500/50"}
                    >
                      <title>{`${s.kind} · checked ${s.checked} · demoted ${d}`}</title>
                    </rect>
                  );
                })}
              </svg>
            );
          })()}
          <div className="space-y-1 max-h-40 overflow-auto">
            {sweeps.map((s, i) => {
              const when = (() => { try { return new Date(s.at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return s.at; } })();
              return (
                <div key={i} className="flex items-center justify-between gap-2 font-mono text-[10px]" data-testid={`r2-sweep-run-${i}`}>
                  <span className="text-zinc-400 truncate">{when}</span>
                  <span className={`px-1 py-0.5 rounded-sm border ${s.kind === "scheduled" ? "bg-cyan-500/10 text-cyan-300 border-cyan-500/30" : s.kind === "scene-backfill" ? "bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/30" : "bg-zinc-800 text-zinc-400 border-zinc-700"}`}>{s.kind}</span>
                  <span className="text-zinc-500 shrink-0">{s.kind === "scene-backfill" ? "assets" : "checked"} <span className="text-zinc-300">{s.checked}</span></span>
                  <span className="text-zinc-500 shrink-0">{s.kind === "scene-backfill" ? "w/ cuts" : "demoted"} <span className={s.kind === "scene-backfill" ? "text-fuchsia-300" : (s.demoted_count ? "text-red-400" : "text-emerald-400")}>{s.demoted_count}</span></span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
